import { defineStore } from "pinia";

import CITIES from "@/graphql/cities.graphql";
import EMAIL_TEMPLATES from "@/graphql/emailTemplates.graphql";
import PROFESSIONS from "@/graphql/professions.graphql";
import PROFESSION_TASKS from "@/graphql/professionTasks.graphql";

export const useLazyStore = defineStore("lazy", {
  state: () => ({
    cities: [],
    citiesQuery: undefined,
    professions: [],
    professionQuery: undefined,
    professionTasks: [],
    professionTasksQuery: undefined,
    emailTemplates: [],
    emailTemplateQuery: undefined,
  }),
  getters: {
    citiesLoading: (state) => {
      return !!state.citiesQuery;
    },
    citiesLoaded: (state) => {
      return state.cities?.length > 0;
    },
    professionsLoading: (state) => {
      return !!state.professionQuery;
    },
    professionsLoaded: (state) => {
      return state.professions?.length > 0;
    },
    professionsCount: (state) => {
      return state.professions?.length ?? 0;
    },
    professionsByGroupId: (state) => {
      return state.professions.reduce((acc, profession) => {
        const groupId = profession.professionGroup?.id;
        if (!groupId) {
          return acc;
        }

        if (!acc[groupId]) {
          acc[groupId] = [];
        }

        acc[groupId] = [...acc[groupId], profession];

        return acc;
      }, {});
    },
    professionTasksLoading: (state) => {
      return !!state.professionTasksQuery;
    },
    professionTasksLoaded: (state) => {
      return state.professionTasks?.length > 0;
    },
    professionTasksCount: (state) => {
      return state.professionTasks?.length ?? 0;
    },
    emailTemplatesLoading: (state) => {
      return !!state.emailTemplateQuery;
    },
    emailTemplatesLoaded: (state) => {
      return state.emailTemplates?.length > 0;
    },
  },
  actions: {
    async loadCities() {
      if (this.cities?.length > 0) {
        return;
      }
      if (this.citiesQuery) {
        await this.citiesQuery;
        return;
      }
      try {
        this.citiesQuery = this.$nuxt.$query(CITIES, {});
        const resp = await this.citiesQuery;
        this.cities = resp.cities;
      } catch (e) {
        console.error("loadCities", e);
      } finally {
        this.citiesQuery = undefined;
      }
    },
    async loadProfessions() {
      if (this.professions && this.professions.length > 0) {
        return;
      }
      if (this.professionQuery) {
        await this.professionQuery;
        return;
      }
      try {
        this.professionQuery = this.$nuxt.$query(PROFESSIONS, {});
        const resp = await this.professionQuery;
        this.professions = resp.professions;
      } catch (e) {
        console.error("loadProfessions", e);
      } finally {
        this.professionQuery = undefined;
      }
    },
    async loadProfessionTasks() {
      if (this.professionTasks && this.professionTasks.length > 0) {
        return;
      }
      if (this.professionTasksQuery) {
        await this.professionTasksQuery;
        return;
      }
      try {
        this.professionTasksQuery = this.$nuxt.$query(PROFESSION_TASKS, {});
        const resp = await this.professionTasksQuery;
        this.professionTasks = resp.professionTasks;
      } catch (e) {
        console.error("loadProfessionTasks", e);
      } finally {
        this.professionTasksQuery = undefined;
      }
    },
    async loadEmailTemplates() {
      if (this.emailTemplates && this.emailTemplates.length > 0) {
        return;
      }
      if (this.emailTemplateQuery) {
        await this.emailTemplateQuery;
        return;
      }
      try {
        this.emailTemplateQuery = this.$nuxt.$query(EMAIL_TEMPLATES, {});
        const resp = await this.emailTemplateQuery;
        this.emailTemplates = resp.emailTemplates;
      } catch (e) {
        console.error("loadEmailTemplates", e);
      } finally {
        this.emailTemplateQuery = undefined;
      }
    },
  },
});
